import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useOutletContext } from "react-router-dom";
import api from "../lib/api";
import { useT, useUI } from "../lib/i18n";
import SEO from "../components/site/SEO";

export default function Gallery() {
  const { content } = useOutletContext();
  const t = useT();
  const ui = useUI();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("tous");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CATEGORIES = [
    { id: "tous", label: ui.allGallery },
    { id: "famille", label: ui.famille },
    { id: "couple", label: ui.couple },
    { id: "naissance", label: ui.naissance },
    { id: "grossesse", label: ui.grossesse },
  ];

  const filtered = filter === "tous" ? items : items.filter((g) => g.category === filter);
  const seo = (content.seo || {}).gallery;

  return (
    <div data-testid="gallery-page">
      <SEO seoData={seo} />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-10">
        <p className="overline mb-4">{ui.galleryLabel}</p>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none font-light">{ui.capturedMoments}</h1>
        <p className="mt-6 text-[#4A3F35] max-w-2xl">{ui.galleryIntro}</p>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setFilter(c.id)} className={`px-5 py-2.5 rounded-full text-sm transition border ${filter === c.id ? "bg-[#2D2A26] text-[#FDFBF7] border-[#2D2A26]" : "bg-transparent text-[#2D2A26] border-[#2D2A26]/20 hover:border-[#2D2A26]"}`} data-testid={`gallery-filter-${c.id}`}>{c.label}</button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="masonry">
          <AnimatePresence>
            {filtered.map((g, i) => (
              <motion.div key={g.id} layout initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6, delay: i * 0.04 }} className="cursor-pointer group overflow-hidden rounded-2xl" onClick={() => setLightbox(g)} data-testid={`gallery-item-${i}`}>
                <img src={g.image_url} alt={t(g.caption)} className="w-full block group-hover:scale-105 transition-transform duration-700" />
                {t(g.caption) && <div className="mt-2 text-xs text-[#4A3F35]/60 px-1">{t(g.caption)}</div>}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        {filtered.length === 0 && (<div className="text-center py-20 text-[#4A3F35]/60">{ui.noPhotos}</div>)}
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLightbox(null)} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-8 cursor-zoom-out">
            <motion.img initial={{ scale: 0.95 }} animate={{ scale: 1 }} src={lightbox.image_url} alt={t(lightbox.caption)} className="max-w-full max-h-full object-contain" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
